import React from 'react';
import PropTypes from 'prop-types';

/**
 * A component to embed a Microsoft Power App canvas app using an iframe.
 * @param {object} props - The component props.
 * @param {string} props.appId - The GUID of the Power App to embed.
 * @param {string} props.tenantId - The GUID of your Azure AD tenant.
 * @param {object} [props.params={}] - An object of key-value pairs to pass as URL parameters to the app.
 * @param {string} [props.className] - Optional CSS class for styling the iframe container.
 * @param {string} [props.title='Power App'] - The title for the iframe for accessibility.
 */
type PowerAppEmbedProps = {
  appId: string;
  tenantId: string;
  params?: Record<string, string>;
  className?: string;
  title?: string;
};

function PowerAppEmbed({
  appId,
  tenantId,
  params = {},
  className = '',
  title,
}: PowerAppEmbedProps) {
  // 1. Base URL for playing a Power App
  const baseUrl = `https://apps.powerapps.com/play/${appId}?tenantId=${tenantId}`;

  // 2. Create a query string from the params object
  // For example, { userName: 'Alice', theme: 'dark' } becomes '&userName=Alice&theme=dark'
  const paramString = Object.entries(params)
    .map(([key, value]) => `&${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('');

  // 3. Combine the base URL and the parameter string
  const appUrl = `${baseUrl}${paramString}&hideNavBar=true`;

  return (
    <div className={`powerapp-embed-container ${className}`} style={{height: '100%'}}>
      <iframe
        src={appUrl}
        title={title}
        style={{ width: '100%', height: '100%', border: 'none' }}
        allow="camera; microphone; geolocation; fullscreen; " // Permissions the app might need
      />
    </div>
  );
}

PowerAppEmbed.propTypes = {
  appId: PropTypes.string.isRequired,
  tenantId: PropTypes.string.isRequired,
  params: PropTypes.object,
  className: PropTypes.string,
  title: PropTypes.string,
};

export default PowerAppEmbed;